'use client'

import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '../../lib/utils'

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

const tooltipStyles: React.CSSProperties = {
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  color: '#ffffff',
  padding: '12px 16px',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.6,
  letterSpacing: '-0.01em',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  maxWidth: '280px',
  zIndex: 999999,
}

const arrowStyles: React.CSSProperties = {
  fill: '#1e293b',
}

function TooltipContent({
  className,
  sideOffset = 8,
  side = 'top',
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const [container, setContainer] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    // Create a dedicated container for tooltips at the end of body
    let tooltipRoot = document.getElementById('tooltip-root')
    if (!tooltipRoot) {
      tooltipRoot = document.createElement('div')
      tooltipRoot.id = 'tooltip-root'
      tooltipRoot.style.cssText = 'position: fixed; top: 0; left: 0; z-index: 999999; pointer-events: none;'
      document.body.appendChild(tooltipRoot)
    }
    setContainer(tooltipRoot)
  }, [])

  if (!container) return null

  return (
    <TooltipPrimitive.Portal container={container}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        side={side}
        sideOffset={sideOffset}
        avoidCollisions
        collisionPadding={12}
        style={tooltipStyles}
        className={cn(
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2',
          'data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow width={12} height={6} style={arrowStyles} />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
